import { mapRboConfigFields } from '@openx/components-deal/lib/utils/mapRboConfig.ts';
import { type DealParticipantApi, type DealState, type OADealState, pmpDealType } from '@openx/types/deal.ts';
import { BUDGET_MODE, MONETIZING_FIELDS, type MonetizingApi } from '@openx/types/monetizing.ts';
import type { AudienceStatus } from '@openx/types/targeting/audience.ts';
import { AMAZON_DEMAND_PARTNER_ID } from '@openx/utils/services/config.ts';
import { transformPlatformShareToPercent } from '../../../configs/deals/utils/platformShareTransform.ts';
import type { GetDealQuery } from '../../../graphql/query/deals/GetDeal.generated.tsx';

type DealQuery = GetDealQuery['dealById'] | null | undefined;

const processDeal = (
  dealRaw: NonNullable<DealQuery>,
  demandPartnerIds: Record<string, string>,
  monetizingData: null | MonetizingApi = null
) => {
  const exchDeal = dealRaw.exchange_deal;

  const { dv360DemandPartnerId } = demandPartnerIds || {};
  const isDV360DemandPartner = exchDeal.deal_participants.some(
    ({ demand_partner }) => demand_partner === dv360DemandPartnerId
  );

  const dealKeyValuesMapping = {
    deal_participants: (val: DealParticipantApi[]) => {
      if (!val?.[0]) {
        return [
          {
            brand_ids: [],
            buyer_id: null,
            buyer_ids: [],
            demand_partner: null,
            main_buyer_id: null,
          },
        ];
      } else {
        return val.map(participant => {
          return {
            ...participant,
            ...(Object.keys(participant.buyer_ids as Record<string, unknown>).length === 1 &&
              isDV360DemandPartner &&
              !exchDeal.enable_dv360_inventory_package && {
                buyer_id: Object.keys(participant.buyer_ids as Record<string, unknown>).toString(),
              }),
            brand_ids: participant.brand_ids ? Object.keys(participant.brand_ids) : [],
            buyer_ids: participant.buyer_ids
              ? Object.keys(participant.buyer_ids).filter(id => id !== participant.main_buyer_id)
              : [],
            amazon_sync_marketplace_enabled:
              participant.demand_partner === AMAZON_DEMAND_PARTNER_ID &&
              exchDeal.synchronized_with?.amazon_sync !== null &&
              exchDeal.synchronized_with?.amazon_sync !== undefined &&
              Object.keys(participant.buyer_ids ?? {}).filter(id => id !== participant.main_buyer_id).length === 0,
          };
        });
      }
    },
    lifetime_impression_goal: (val: string | number | null) => val ?? 0,
    open_auction_access: (val: string) => val ?? '',
    use_market_floor: (val: string) => (val === null ? false : val === '1'),
  };

  const monetizing: Pick<
    DealState,
    | 'monetizing_budget'
    | 'monetizing_region'
    | 'pacing_mode'
    | 'is_pacing_enabled'
    | 'budget_type'
    | 'budget_mode'
    | 'cadence_type'
  > = {
    budget_mode: null,
    budget_type: null,
    cadence_type: null,
    is_pacing_enabled: !!exchDeal?.monetizing_uid,
    monetizing_budget: '',
    monetizing_region: null,
    pacing_mode: null,
  };

  if (monetizingData) {
    monetizing.monetizing_region = monetizingData[MONETIZING_FIELDS.REGION];
    monetizing.pacing_mode = monetizingData[MONETIZING_FIELDS.PACING_MODE];
    monetizing.budget_type = monetizingData[MONETIZING_FIELDS.BUDGET_TYPE];
    monetizing.budget_mode = monetizingData[MONETIZING_FIELDS.BUDGET]
      ? BUDGET_MODE.CAMPAIGN_FLIGHT_TIME
      : BUDGET_MODE.CADENCE;

    if (monetizingData.budget === undefined || monetizingData.budget === null) {
      monetizing.monetizing_budget = monetizingData.cadences[0].budget.toString();
      monetizing.cadence_type = monetizingData.cadences[0].type;
    } else {
      monetizing.monetizing_budget = monetizingData.budget.toString();
    }
  }

  const dealState: OADealState = {
    ...exchDeal,
    ...monetizing,
    pmp_deal_type: pmpDealType.WithinOpenAuction,
    status: exchDeal.status,
    type: exchDeal.type,
    type_full: exchDeal.type_full,
    segment_partner_revenue_method: exchDeal.segment_partner_revenue_method ?? null,
    segment_partner_platform_share: transformPlatformShareToPercent(exchDeal.segment_partner_platform_share),
    rbo_config: mapRboConfigFields(exchDeal.rbo_config),
    pricing_by_org_data: exchDeal.pricing_by_org_data ?? null,
  };
  Object.keys(dealKeyValuesMapping).forEach(key => {
    dealState[key] = dealKeyValuesMapping[key](exchDeal[key]);
  });

  if (dealRaw._package) {
    dealState.audience_status = (dealRaw._package.audience?.status as AudienceStatus) || null;
  }

  return dealState;
};

export const formatDealState = (
  dealRaw: DealQuery,
  demandPartnerIds: Record<string, string>,
  monetizingData: null | MonetizingApi = null
): OADealState | undefined => {
  if (!dealRaw) {
    return undefined;
  }

  return processDeal(dealRaw, demandPartnerIds, monetizingData);
};
